/**
 * Firewall geometry and rules — shared so the server (placement, damage) and
 * the client (projectile termination, movement colliders) agree exactly.
 *
 * A firewall is an axis-aligned force field placed on one side of the player:
 * the side is the dominant axis of the aim angle (snapped to N/E/S/W), so it is
 * always a plain rectangle — the flat world has no rotated colliders on the
 * client and this keeps it that way. Players and enemies walk through it.
 * Enemy projectiles from either side are stopped; the owner (and, in team
 * modes, teammates) shoot straight through.
 */

export const FIREWALL_DEFAULTS = Object.freeze({
  length: 240,      // along the wall
  thickness: 20,    // across it
  offset: 80,       // from the player's centre to the wall's centre (radius 40 + gap)
  hp: 120,          // sniper: 2 shots, rifle: 5
  lengthPerStack: 40
});

/** Snap an aim angle (radians, 0 = +x, y down) to one of four sides. */
export function snapDirection(angle) {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  if (Math.abs(c) >= Math.abs(s)) return c >= 0 ? { x: 1, y: 0 } : { x: -1, y: 0 };
  return s >= 0 ? { x: 0, y: 1 } : { x: 0, y: -1 };
}

/**
 * Where a firewall goes for a player at (x, y) aiming at `angle`.
 * @returns {{ x, y, width, height, dir }} centre + axis-aligned size
 */
export function firewallPlacement(x, y, angle, { length, thickness, offset } = FIREWALL_DEFAULTS) {
  const dir = snapDirection(angle);
  const cx = x + dir.x * offset;
  const cy = y + dir.y * offset;
  // The wall runs perpendicular to the facing direction.
  const width = dir.x !== 0 ? thickness : length;
  const height = dir.x !== 0 ? length : thickness;
  return { x: cx, y: cy, width, height, dir };
}

/** Clamp a placement so the whole wall stays inside the map. */
export function clampToBounds(p, mapWidth, mapHeight, margin = 4) {
  const hw = p.width / 2;
  const hh = p.height / 2;
  return {
    ...p,
    x: Math.min(Math.max(p.x, hw + margin), mapWidth - hw - margin),
    y: Math.min(Math.max(p.y, hh + margin), mapHeight - hh - margin)
  };
}

/**
 * Should a shot from `shooter` pass through this wall?
 * Owner always; teammates when both have a team and it matches.
 */
export function passesThrough(wall, shooterId, shooterTeam) {
  if (!wall?.passThroughFor) return false;
  if (wall.passThroughFor.ownerId === shooterId) return true;
  const team = wall.passThroughFor.team;
  return team !== null && team !== undefined && shooterTeam !== null && shooterTeam !== undefined && team === shooterTeam;
}

/** The world object a firewall is published as (server → clients). */
export function firewallWorldObject(id, placement, { ownerId, team = null, hp, expiresAt }) {
  return {
    id,
    objectType: 'firewall',
    type: 'firewall',
    x: placement.x,
    y: placement.y,
    width: placement.width,
    height: placement.height,
    rotation: 0,
    visible: true,
    collidable: true,
    isSensor: true,
    collision: [{ x: 0, y: 0, width: placement.width, height: placement.height, type: 'wall', label: 'collision' }],
    passThroughFor: { ownerId, team },
    hp,
    maxHp: hp,
    expiresAt
  };
}
